import { useState, useCallback, useRef } from 'react'
import { CSSTransition, TransitionGroup } from 'react-transition-group'
import classNames from 'classnames'

import { languageSelector, languageObjectSelector } from 'i18n/selectors'
import { componentSelector, languagesSelector } from 'wordpress/selectors'
import featureToggles from 'shared/featureToggles'
import useClickOutside from 'shared/hooks/useClickOutside'

import { AppStoreBadge, GooglePlayStoreBadge } from '../../shared/components/AppDownloadBadge'

import * as constants from '../constants'
import { headerStateSelector } from '../selectors'
import {
    setHamburgerDropdownStateMain,
    setHamburgerDropdownStateGetApp,
    setHamburgerDropdownStateLanguages,
} from '../actions'

import DropdownLink from './DropdownLink'
import DropdownButton from './DropdownButton'
import Flag from './Flag'

import '../styles/animations.scss'

const HamburgerDropdown = ({ children }) => <div className="arrow-box">{children}</div>

const ContentMain = ({ promotions, items, onGetAppClick, onLanguagesClick, closeMenu }) => (
    <ul>
        {promotions && (
            <li>
                <DropdownLink
                    url={promotions.page}
                    classNames={{
                        'nav-dropdown-special-link': true,
                        'nav-user-dropdown-button': true,
                    }}
                    onClick={closeMenu}>
                    {promotions.label}
                </DropdownLink>
            </li>
        )}
        {Object.keys(items).map(key => (
            <li key={key}>
                <DropdownLink
                    isExternalLink={!!items[key].url}
                    url={items[key].url || items[key].pageSlug || items[key].page}
                    classNames={{
                        'nav-user-dropdown-button': true,
                    }}
                    onClick={closeMenu}>
                    {items[key].label}
                </DropdownLink>
            </li>
        ))}
        {featureToggles.showDownloadApp && (
            <li>
                <DropdownButton onClick={onGetAppClick}>
                    <T>get.app.title</T>
                </DropdownButton>
            </li>
        )}
        <li>
            <DropdownButton onClick={onLanguagesClick}>Language</DropdownButton>
        </li>
    </ul>
)

const ContentSecondary = ({ onBackClick, children }) => (
    <ul className="nav-languages">
        <li>
            <DropdownButton onClick={onBackClick}>
                <img alt={'Back'} src={require('../icons/ic-menu-back.svg')} />
            </DropdownButton>
        </li>
        {children}
    </ul>
)

const ContentGetApp = ({ onBackClick, closeMenu }) => (
    <ContentSecondary onBackClick={onBackClick}>
        <li onClick={closeMenu}>
            <AppStoreBadge containerClass="nav-burger-app-link" linkClass="nav-user-dropdown-button">
                <span>
                    <T>ios.app.title</T>
                </span>
            </AppStoreBadge>
        </li>
        <li onClick={closeMenu}>
            <GooglePlayStoreBadge containerClass="nav-burger-app-link" linkClass="nav-user-dropdown-button">
                <span>
                    <T>android.app.title</T>
                </span>
            </GooglePlayStoreBadge>
        </li>
    </ContentSecondary>
)

const $ContentLanguageSwitcher = ({ language, languages, closeMenu, onBackClick }) => (
    <ContentSecondary onBackClick={onBackClick}>
        {Object.values(languages).map(({ flag, name, slug }) => (
            <li key={slug}>
                <DropdownLink
                    language={slug}
                    className={`nav-user-dropdown-button ${slug === language ? 'active' : ''}`}
                    onClick={closeMenu}>
                    <Flag flag={flag} name={name} />
                    <span className="label">{name}</span>
                </DropdownLink>
            </li>
        ))}
    </ContentSecondary>
)

const ContentLanguageSwitcher = connect(
    createStructuredSelector({
        language: languageObjectSelector,
        languages: languagesSelector,
    })
)($ContentLanguageSwitcher)
const HamburgerContainer = ({
    navItems: { content: _, promotions, ...rest },
    header,
    setHamburgerDropdownStateGetApp,
    setHamburgerDropdownStateLanguages,
    setHamburgerDropdownStateMain,
}) => {
    const [isHamburgerOpen, setIsHamburgerOpen] = useState(false)
    const ref = useRef()
    const transitionComponentRef = useRef()

    const toggleHamburger = useCallback(() => {
        setIsHamburgerOpen(prevState => !prevState)
    }, [])

    const closeHamburger = useCallback(() => {
        setIsHamburgerOpen(false)
    }, [])

    useClickOutside(ref, closeHamburger)

    let content = null
    switch (header.hamburgerDropdownState) {
        case constants.HAMBURGER_DROPDOWN_STATE_MAIN:
            content = (
                <ContentMain
                    promotions={promotions}
                    items={rest}
                    onGetAppClick={setHamburgerDropdownStateGetApp}
                    onLanguagesClick={setHamburgerDropdownStateLanguages}
                    closeMenu={closeHamburger}
                />
            )
            break
        case constants.HAMBURGER_DROPDOWN_STATE_GET_APP:
            content = <ContentGetApp onBackClick={setHamburgerDropdownStateMain} closeMenu={closeHamburger} />
            break
        case constants.HAMBURGER_DROPDOWN_STATE_LANGUAGES:
            content = <ContentLanguageSwitcher onBackClick={setHamburgerDropdownStateMain} closeMenu={closeHamburger} />
            break
        default:
            break
    }

    const hamburgerClass = classNames({
        hamburger: true,
        'hamburger--spin': true,
        'is-active': isHamburgerOpen,
    })

    return (
        <div className="nav-hamburger-container" ref={ref}>
            <button
                className={hamburgerClass}
                type="button"
                onClick={toggleHamburger}
                aria-label="Toggle hamburger menu"
                aria-controls="menu"
                aria-expanded={isHamburgerOpen}>
                <span className="hamburger-box">
                    <span className="hamburger-inner" />
                </span>
            </button>

            <TransitionGroup>
                {isHamburgerOpen && (
                    <CSSTransition
                        nodeRef={transitionComponentRef}
                        key="hamburger-dropdown"
                        timeout={{ enter: 100, exit: 100 }}
                        classNames="nav-dropdown-transition">
                        <div ref={transitionComponentRef}>
                            <HamburgerDropdown>{content}</HamburgerDropdown>
                        </div>
                    </CSSTransition>
                )}
            </TransitionGroup>
        </div>
    )
}

const mapStateToProps = createStructuredSelector({
    navItems: componentSelector('header'),
    language: languageSelector,
    header: headerStateSelector,
})

const mapDispatchToProps = {
    setHamburgerDropdownStateGetApp,
    setHamburgerDropdownStateLanguages,
    setHamburgerDropdownStateMain,
}

export default connect(mapStateToProps, mapDispatchToProps)(HamburgerContainer)
